const STORAGE_KEY = 'homelocal_favorites'

/**
 * Same-tab change signal, dispatched on `window` after every successful write.
 * Each widget IIFE bundles its OWN copy of this module (vite.config.ts
 * `lib.formats: ['iife']`), so a widget in the same tab but a different
 * bundle can only observe writes through this CustomEvent (the native
 * `storage` event fires cross-tab only). `saveFavorites` is the single
 * serializer, so exactly one event fires per write regardless of writer —
 * see `common/guest/favorites.ts` (#1138) for the subscription helper.
 */
export const FAVORITES_CHANGED_EVENT = 'hr-favorites-changed'

function getFavorites(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? raw.split(',').filter(Boolean) : []
  } catch {
    return []
  }
}

function saveFavorites(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, ids.join(','))
  } catch {
    // localStorage unavailable — nothing was written, so signal nothing.
    return
  }
  try {
    window.dispatchEvent(new CustomEvent(FAVORITES_CHANGED_EVENT))
  } catch {
    // No window/CustomEvent (SSR) — the write itself still stands.
  }
}

export function isFavorite(id: string): boolean {
  return getFavorites().includes(id.toString())
}

export function addFavorite(id: string) {
  const favs = getFavorites()
  if (!favs.includes(id.toString())) {
    favs.push(id.toString())
    saveFavorites(favs)
  }
}

export function removeFavorite(id: string) {
  const favs = getFavorites()
  const idx = favs.indexOf(id.toString())
  if (idx > -1) {
    favs.splice(idx, 1)
    saveFavorites(favs)
  }
}

export function toggleFavorite(id: string): boolean {
  if (isFavorite(id)) {
    removeFavorite(id)
    return false
  }
  addFavorite(id)
  return true
}
